import { FormEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DaumPostcodeEmbed from "react-daum-postcode";
import { twMerge } from "tailwind-merge";
import toast, { Toaster } from "react-hot-toast";
import { PatternFormat } from "react-number-format";
import Button from "./common/Button";
import { AddressItem, usePostAddressBook } from "hooks/morning/usePostAddressBook";
import { usePostAddress } from "hooks/morning/usePostAddress";
import { usePutAddress } from "hooks/morning/usePutAddress";
import { useDeleteAddress } from "hooks/morning/useDeleteAddress";
import { useMediaQuery } from "hooks/utils/useMediaQuery";
import { AddressViewResult } from "hooks/morning/useGetAddressView";
import { formatTelNumber, toastErrorSyle } from "hooks/utils/util";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { ReactComponent as Search } from "assets/morning/icon/search.svg";
import { ReactComponent as Checked } from "assets/morning/icon/checked.svg";

interface AddressBookProps {
    onClose: () => void;
    onSelect: (item: AddressItem) => void;
    className?: string;
    isManage?: boolean;
    buttonPlacement?: "top" | "bottom";
}
interface AddressRegistrationProps {
    isModify?: boolean;
    isPopup?: boolean;
    data: AddressViewResult | null;
    done: () => void;
}
type SelectAddressProps = {
    // onSubmitSearch: (searchText: string) => void;
    handleSelect: (value: AddressItem) => void;
    value?: number;
};

export type SelectAddressHandle = {
    handleReset: () => void;
};

export const SelectAddress = forwardRef<SelectAddressHandle, SelectAddressProps>(({ handleSelect, value }, ref) => {
    const [searchTxt, setSearchTxt] = useState<string>("");
    const [addressList, setAddressList] = useState<AddressItem[]>([]);
    const { mutate, isPending } = usePostAddressBook(data => {
        setAddressList(data);
    });

    useEffect(() => {
        mutate({ searchText: searchTxt });
    }, [searchTxt]);

    const onSubmitSearch = (searchTxt: string) => {
        mutate({ searchText: searchTxt });
    };

    const handleReset = () => {
        setSearchTxt("");
        // mutate({ searchText: "" });
    };

    // 👇 외부에서 handleReset을 쓸 수 있게 함
    useImperativeHandle(ref, () => ({
        handleReset,
    }));

    return (
        <div className="flex flex-col gap-5 max-h-[65vh]">
            <form
                onSubmit={e => {
                    e.preventDefault();
                    onSubmitSearch(searchTxt);
                }}
                className="flex-1 border bg-[#F7F7F8] text-[18px] py-[15px] rounded-lg flex items-center px-3 [&:has(input:focus)]:ring-2 ring-[#FF7B2E] ring-inset"
            >
                <input
                    value={searchTxt}
                    onChange={e => setSearchTxt(e.target.value)}
                    className="bg-transparent mr-9 px-1 w-full outline-0 focus:outline-none"
                    placeholder="주소록 검색"
                />

                <Search className="cursor-pointer" onClick={() => onSubmitSearch(searchTxt)} />
            </form>

            <div className="flex-1 overflow-y-auto flex flex-col gap-[8px]">
                {isPending ? (
                    <div className="text-center py-10">검색중입니다...</div>
                ) : (
                    <>
                        {addressList.length === 0 ? (
                            <div className="text-center py-10">결과가 없습니다.</div>
                        ) : (
                            <>
                                {addressList.map(item => {
                                    const idx = item.idx.toString();
                                    return (
                                        <label
                                            key={idx}
                                            className={`border py-4 px-5 gap-x-[18px] flex rounded-lg items-center [&:has(input:checked)]:bg-white`}
                                            htmlFor={idx}
                                        >
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name="addr"
                                                    value={idx}
                                                    id={idx}
                                                    //   defaultChecked={
                                                    //       item.value === infoData.c_addr_defalt
                                                    //   }
                                                    checked={item.idx === value}
                                                    onChange={() => handleSelect(item)}
                                                    className="peer appearance-none"
                                                />
                                                <Checked className="w-8 h-8 text-new peer-checked:!block hidden" />
                                                <div className="w-[19px] h-[19px] border-gray-300 mx-[6.5px] rounded-full border-2 peer-checked:hidden" />

                                                <div className="flex flex-col w-[440px]">
                                                    <p className="text-[18px] text-[#1B1C1E] font-[500]">{item.a_company}</p>
                                                    <p className="text-[18px] text-[#666]">{item.a_tel}</p>
                                                    <p className="text-[#1B1C1E]">
                                                        {item.a_address} {item.a_address_detail}
                                                    </p>
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

export const AddressRegistration = ({ data, done, isModify, isPopup = false }: AddressRegistrationProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [cursorPos, setCursorPos] = useState<number | null>(null);
    const [showPostcode, setShowPostcode] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [registerdata, setRegisterData] = useState({
        idx: data?.idx?.toString() ?? null,
        company: data?.a_company ?? "",
        tel: data?.a_tel ?? "",
        name: data?.a_name ?? "",
        mobile: data?.a_mobile ?? "",
        postcode: data?.a_post ?? "",
        address: data?.a_address ?? "",
        addressDetail: data?.a_address_detail ?? "",
    });

    // 새로고침시
    useEffect(() => {
        setRegisterData({
            idx: data?.idx?.toString() ?? null,
            company: data?.a_company ?? "",
            tel: data?.a_tel ?? "",
            name: data?.a_name ?? "",
            mobile: data?.a_mobile ?? "",
            postcode: data?.a_post ?? "",
            address: data?.a_address ?? "",
            addressDetail: data?.a_address_detail ?? "",
        });
    }, [data]);

    const { mutate } = usePostAddress();
    const { mutate: modifyMutate } = usePutAddress();

    const handleAddress = (data: any) => {
        let fullAddress = data.roadAddress || data.jibunAddress;

        const extras: string[] = [];

        if (data.bname) extras.push(data.bname);
        if (data.buildingName) extras.push(data.buildingName);

        if (extras.length > 0) {
            fullAddress += ` (${extras.join(", ")})`;
        }

        setRegisterData((prev: any) => ({
            ...prev,
            postcode: data.zonecode,
            address: fullAddress,
        }));

        setErrors(prev => ({
            ...prev,
            postcode: false,
            address: false,
        }));

        setShowPostcode(false);
    };

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();

        const newErrors: { [key: string]: boolean } = {};

        let requiredFields: (keyof typeof registerdata)[] = ["postcode", "address", "company", "tel"];

        requiredFields.forEach(field => {
            const value = registerdata[field];
            if (typeof value === "string") {
                if (!value.trim()) {
                    newErrors[field] = true;
                }
            } else if (value === undefined || value === null) {
                newErrors[field] = true;
            }
        });

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) return;

        const registerPayload = {
            a_company: registerdata.company,
            a_tel: registerdata.tel,
            a_name: registerdata.name,
            a_mobile: registerdata.mobile,
            a_post: registerdata.postcode,
            a_address: registerdata.address,
            a_address_detail: registerdata.addressDetail,
        };
        if (isModify && registerdata.idx) {
            const modifyPayload = {
                ...registerPayload,
                idx: registerdata.idx,
            };
            modifyMutate(modifyPayload, {
                onSuccess: (data: any) => {
                    toast.success("성공적으로 수정 되었습니다.");
                    done();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            });
            return;
        }
        mutate(registerPayload, {
            onSuccess: (data: any) => {
                toast.success("성공적으로 추가 되었습니다.");
                done();
            },
            onError: (error: any) => {
                const message = error.message;
                toast.error(message, toastErrorSyle);
            },
        });
    };

    const handlePhoneNumberChange = (value: string) => {
        const input = inputRef.current;
        if (!input) return;

        const cursorPosition = input.selectionStart ?? value.length;
        const { formatted, cursorOffset } = formatTelNumber(value, registerdata.tel, cursorPosition);

        setCursorPos(cursorPosition + cursorOffset);

        setRegisterData((prev: any) => ({ ...prev, tel: formatted }));
    };

    useEffect(() => {
        if (cursorPos !== null && inputRef.current) {
            inputRef.current.setSelectionRange(cursorPos, cursorPos);
            setCursorPos(null); // 한 번만 조정
        }
    }, [registerdata.tel]);

    return (
        <>
            <form onSubmit={onSubmit} className={`flex flex-col flex-1 overflow-hidden`}>
                <div className={`${isPopup ? "px-[40px]" : "px-[16px]"} py-[20px] flex flex-col gap-[20px] flex-1 overflow-y-auto`}>
                    <div className="flex items-center flex-wrap gap-y-1">
                        <label htmlFor="company" className="w-full max-w-[160px] text-[#1B1C1E] text-[18px] font-[500] text-nowrap">
                            상호명<span className="text-[#FF7B2E] ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            id="company"
                            value={registerdata.company}
                            onChange={e => {
                                setRegisterData((prev: any) => ({ ...prev, company: e.target.value }));
                                setErrors(prev => ({ ...prev, company: false }));
                            }}
                            placeholder="상호명을 직접 입력하거나 조회하세요."
                            className={`flex-1 p-[15px] border border-[#E1E2E4] rounded-[8px] text-[18px] ${errors?.company && "border-red-500 border-2"}`}
                        />
                    </div>
                    <div className="flex items-center flex-wrap gap-y-1">
                        <label htmlFor="tel" className="w-full max-w-[160px] text-[#1B1C1E] text-[18px] font-[500]">
                            연락처<span className="text-[#FF7B2E] ml-1">*</span>
                        </label>
                        <input
                            type="text"
                            id="tel"
                            ref={inputRef}
                            value={registerdata.tel}
                            onChange={e => {
                                handlePhoneNumberChange(e.target.value);
                                setErrors(prev => ({ ...prev, tel: false }));
                            }}
                            placeholder="연락처를 입력해주세요."
                            className={`flex-1 p-[15px] border border-[#E1E2E4] rounded-[8px] text-[18px] ${errors?.tel && "border-red-500 border-2"}`}
                        />
                    </div>
                    <div className="flex items-center flex-wrap gap-y-1">
                        <label htmlFor="name" className="w-full max-w-[160px] text-[#1B1C1E] text-[18px] font-[500]">
                            담당자명
                        </label>
                        <input
                            type="text"
                            id="name"
                            value={registerdata.name}
                            onChange={e => {
                                setRegisterData((prev: any) => ({ ...prev, name: e.target.value }));
                            }}
                            placeholder="담당자명을 입력해주세요."
                            className="flex-1 p-[15px] border border-[#E1E2E4] rounded-[8px] text-[18px]"
                        />
                    </div>

                    <div className="flex items-center flex-wrap gap-y-1">
                        <label htmlFor="mobile" className="w-full max-w-[160px] text-[#1B1C1E] text-[18px] font-[500]">
                            휴대폰 번호
                        </label>
                        <PatternFormat
                            format="###-####-####"
                            id="mobile"
                            name="mobile"
                            value={registerdata.mobile}
                            onChange={e => setRegisterData((prev: any) => ({ ...prev, mobile: e.target.value }))}
                            placeholder="연락처를 입력해주세요."
                            className={`flex-1 p-[15px] border border-[#E1E2E4] rounded-[8px] text-[18px]`}
                        />
                    </div>

                    <div className="flex flex-col gap-[4px]">
                        <label htmlFor="address" className="text-[#1B1C1E] text-[18px] font-[500]">
                            주소<span className="text-[#FF7B2E] ml-1">*</span>
                        </label>
                        <div className="flex gap-[4px] relative">
                            <input
                                disabled
                                type="text"
                                name="postcode"
                                value={registerdata.postcode}
                                onChange={e => {
                                    setRegisterData((prev: any) => ({ ...prev, postcode: e.target.value }));
                                    setErrors(prev => ({ ...prev, postcode: false }));
                                }}
                                className={`w-full flex-1 border border-[#E1E2E4] rounded-[8px] bg-[#F7F7F8] p-[15px] text-[18px] ${errors?.postcode && "border-red-500 border-2"}`}
                            />
                            <button
                                type="button"
                                className="border border-[#1B1C1E] text-[#1B1C1E] text-[18px] rounded-[8px] px-[28px] text-nowrap"
                                onClick={() => setShowPostcode(true)}
                            >
                                우편번호
                            </button>
                        </div>
                        {showPostcode && (
                            <div className="bg-white border w-full rounded-lg shadow-lg overflow-hidden">
                                <div className="px-6 py-5 text-2xl font-bold border-b border-gray3 flex items-center justify-between">
                                    <p>우편번호 찾기</p>
                                    <Close onClick={() => setShowPostcode(false)} className="cursor-pointer" />
                                </div>
                                <DaumPostcodeEmbed className="min-h-[470px]" minWidth={250} onComplete={handleAddress} />
                            </div>
                        )}
                        <input
                            disabled
                            type="text"
                            name="address"
                            value={registerdata.address}
                            onChange={e => {
                                setRegisterData((prev: any) => ({ ...prev, address: e.target.value }));
                                setErrors(prev => ({ ...prev, address: false }));
                            }}
                            className={`border border-[#E1E2E4] rounded-[8px] bg-[#F7F7F8] p-[15px] text-[18px] ${errors?.address && "border-red-500 border-2"}`}
                        />
                        <input
                            type="text"
                            name="addressDetail"
                            value={registerdata.addressDetail}
                            onChange={e => {
                                setRegisterData((prev: any) => ({ ...prev, addressDetail: e.target.value }));
                                // setErrors(prev => ({ ...prev, addressDetail: false }));
                            }}
                            placeholder="상세주소"
                            className="border border-[#E1E2E4] rounded-[8px] bg-transparent p-[15px] text-[18px]"
                        />
                    </div>
                </div>
                <button type="submit" className="text-white font-bold text-[20px] bg-[#132E47] w-full py-[15px]">
                    저장
                </button>
            </form>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
};

type RegisterStateProps = { open: boolean; modify: boolean; data: null | AddressViewResult };
const initialRegisterState = { open: false, modify: false, data: null };

export const AddressList = ({ onClose, onSelect, className, isManage = false, buttonPlacement = "bottom" }: AddressBookProps) => {
    const wrapperStyle = twMerge("flex flex-col px-[4%] py-5 gap-5 max-h-[70vh]", className);
    const [searchTxt, setSearchTxt] = useState<string>("");
    const [addressList, setAddressList] = useState<AddressItem[]>([]);
    const [register, setRegister] = useState<RegisterStateProps>(initialRegisterState);
    const responsive450 = useMediaQuery(450);
    const navigate = useNavigate();

    const { mutate, isPending } = usePostAddressBook(data => {
        setAddressList(data);
    });
    const { mutate: deleteAddress } = useDeleteAddress(() => mutate({ searchText: searchTxt }));

    useEffect(() => {
        mutate({ searchText: searchTxt });
    }, [searchTxt]);

    // 주소 등록/수정 성공시 목록 재요청
    const handleRegisterAddress = () => {
        mutate({ searchText: searchTxt });
    };

    // 주소 삭제
    const handleDeleteAddress = (idx: number) => {
        // 컨펌 팝업 추가하기
        deleteAddress({ idx: idx.toString() });
    };

    return (
        <>
            <div className={wrapperStyle}>
                {buttonPlacement === "top" && (
                    <Button
                        color="new"
                        className="w-full max-w-[600px] mx-auto min-h-[56px] text-[18px]"
                        onClick={() =>
                            responsive450 ? navigate("/mypage/address/register") : setRegister({ open: true, modify: false, data: null })
                        }
                    >
                        주소록 추가
                    </Button>
                )}

                <form
                    onSubmit={e => {
                        e.preventDefault();
                        mutate({ searchText: searchTxt });
                    }}
                    className="flex-1 border bg-[#F7F7F8] text-[18px] py-[15px] rounded-lg flex items-center px-3 [&:has(input:focus)]:ring-2 ring-[#FF7B2E] ring-inset"
                >
                    <input
                        value={searchTxt}
                        onChange={e => setSearchTxt(e.target.value)}
                        className="bg-transparent mr-9 px-1 w-full outline-0 focus:outline-none"
                        placeholder="주소록 검색"
                    />

                    <Search className="cursor-pointer" onClick={() => mutate({ searchText: searchTxt })} />
                </form>

                <div className="flex-1 overflow-y-auto flex flex-col gap-[8px]">
                    {isPending ? (
                        <div className="text-center py-10">검색중입니다...</div>
                    ) : (
                        <>
                            {addressList.map(item => (
                                <div key={item.idx} className="border py-4 px-5 gap-4 justify-between flex rounded-lg items-center">
                                    <div className="flex flex-col w-[440px]">
                                        <p className="text-[18px] text-[#1B1C1E] font-[500]">
                                            {item.a_company}
                                            {/* {item.a_code && <span className="text-new ml-2">{item.a_code}</span>} */}
                                        </p>
                                        <p className="text-[18px] text-[#666]">{item.a_tel}</p>
                                        <p className="text-[#1B1C1E]">
                                            {item.a_address} {item.a_address_detail}
                                        </p>
                                    </div>
                                    <div className="w-[104px] flex flex-col gap-2">
                                        {!isManage && (
                                            <Button
                                                className="w-full bg-new border-none text-white"
                                                onClick={() => {
                                                    onSelect(item);
                                                    onClose();
                                                }}
                                            >
                                                사용
                                            </Button>
                                        )}
                                        <div className="flex w-full gap-1 ">
                                            <Button
                                                className="w-[50px] h-[33px] text-sm whitespace-nowrap"
                                                onClick={() =>
                                                    responsive450
                                                        ? navigate(`/mypage/address/register/${item.idx}`)
                                                        : setRegister({ open: true, modify: true, data: { ...item, c_code: "" } })
                                                }
                                            >
                                                수정
                                            </Button>
                                            <Button
                                                className={`w-[50px] h-[33px] text-sm whitespace-nowrap ${isManage && "bg-[#F3F5F8]"}`}
                                                onClick={() => handleDeleteAddress(item.idx)}
                                            >
                                                삭제
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {buttonPlacement === "bottom" && (
                    <Button
                        color="new"
                        className="w-full max-w-[600px] mx-auto min-h-[56px] text-[18px]"
                        onClick={() =>
                            responsive450 ? navigate("/mypage/address/register") : setRegister({ open: true, modify: false, data: null })
                        }
                    >
                        주소록 추가
                    </Button>
                )}
            </div>
            {/* 주소록 등록/수정 팝업 */}
            {register.open && (
                <div className="max-h-[100dvh] fixed inset-0 z-[201] bg-black bg-opacity-40 flex items-center justify-center overflow-hidden">
                    <div className="bg-white w-full max-w-[695px] max-h-[100dvh] rounded-xl shadow-xl relative flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-10 pt-6 pb-5">
                            <p className="text-2xl font-bold">주소록 추가/수정</p>
                            <Close className="cursor-pointer" onClick={() => setRegister(initialRegisterState)} />
                        </div>
                        <hr />
                        <AddressRegistration
                            data={register.data}
                            done={() => {
                                handleRegisterAddress();
                                setRegister(initialRegisterState);
                            }}
                            isPopup
                            isModify={register.data?.idx ? true : false}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

const AddressBook = ({ onClose, onSelect }: AddressBookProps) => {
    return (
        <div className="bg-white w-full max-w-[695px] max-h-[80vh] relative h-full rounded-xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-[4%] pt-6 pb-5">
                <p className="text-2xl font-bold">주소록</p>
                <Close className="cursor-pointer" onClick={onClose} />
            </div>
            <hr />
            <AddressList onClose={onClose} onSelect={onSelect} />
        </div>
    );
};

export default AddressBook;
